/**
 * Projekt-Werkstatt (Modul 08, Stylebook-Lücke L7) — Port der Web-Renderer
 * (app.js: werkstattUebersicht, projektSegmente, schrittHtml, promptKarte,
 * benoetigtHtml, anpassHinweis). Die Schritt-Seiten entstehen als HTML-Strings
 * mit denselben Klassen/data-Attributen wie im Web — Optik kommt aus dem
 * gespiegelten CSS, Klicks aus der Lesson-Delegation (lesson-interactions.ts).
 *
 * Die Übersichts-Karten sind eine Preact-Komponente: die App kennt (anders als
 * das Web) nicht alle Kapiteltexte lokal — Details (Ergebnis/Lernziele) werden
 * beim Aufklappen einer Karte nachgeladen (Kapitel-Cache greift danach).
 */
import { useEffect, useState } from "preact/hooks";
import type { CourseModule, ChapterRef } from "../data/types";
import type { WkPersonalisierung } from "../api/types";
import type { UkSegment } from "./unterkapitel";
import { loadChapter } from "../data/course-store";
import { sanitizeHtml } from "./sanitize";
import {
  personalText,
  ladePersonal,
  ladeSchritt,
  setzeSchritt,
  istSchrittPlatzhalter,
} from "./werkstatt-personal";
import { isAuthenticated } from "../state/session";

/** Kontext (Modul/Block), den die Personalisierung zum Nachschlagen braucht. */
interface WkCtx {
  mId: string;
  bId: string;
}

/* ---------- HTML-Helfer (wie esc/escAttr im Web) ---------- */
const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escAttr = (s: unknown): string => esc(s).replace(/"/g, "&quot;");

/* ---------- Roh-Felder eines Projekt-Kapitels (Chapter-API, dict(block)) ---------- */
interface WkZugang {
  dienst?: string;
  wofuer?: string;
  konto?: string;
  mindeststufe?: string;
  alternativen?: string[];
}
interface WkAusgabeKi {
  typ?: string;
  empfohlen?: string;
  mehr?: string;
  alternativen?: string[];
}
interface WkSteckbrief {
  endprodukt?: string;
  schwierigkeit?: number;
  minuten?: number;
  modellklasse?: string;
  thinking?: string;
  benoetigt?: string[] | { koennen?: string[]; mitbringen?: string[]; zugaenge?: WkZugang[]; ausgabe_kis?: WkAusgabeKi[] };
}
interface WkPrompt {
  id?: string;
  text?: string;
  ziel_ki?: string;
  variablen?: string[];
}
interface WkSchritt {
  nr?: number | string;
  titel?: string;
  ziel?: string;
  prompt?: WkPrompt;
  bewirkt?: string;
  muster?: string;
  verhindert?: string;
  achten?: string;
  hinweis?: string;
}
export interface ProjektPayload {
  id: string;
  titel?: string;
  title?: string;
  summary?: string;
  layout?: string;
  duration_minutes?: number;
  steckbrief?: WkSteckbrief;
  was_du_lernst?: string[];
  schritte?: WkSchritt[];
  material?: { titel?: string; text?: string; html?: string } | null;
  probier?: { titel?: string; text_html?: string; prompt_naiv?: WkPrompt } | null;
  transfer?: { text_html?: string; frage?: string } | null;
  personalisierung?: WkPersonalisierung | null;
  intro_html?: string;
  text?: string;
  [k: string]: unknown;
}

/* ---------- Meta-Badges (Sterne, Dauer, Modellklasse) ---------- */
function sterneHtml(n: unknown): string {
  const max = 3;
  const val = Math.max(0, Math.min(max, Number(n) || 0));
  let out = "";
  for (let i = 0; i < max; i++) out += `<span class="wk-star${i < val ? " on" : ""}" aria-hidden="true">★</span>`;
  return `<span class="wk-stars" role="img" aria-label="Schwierigkeit ${val} von ${max}">${out}</span>`;
}
function projektMeta(b: ProjektPayload): string[] {
  const sb = b.steckbrief || {};
  const teile: string[] = [];
  if (sb.schwierigkeit != null) teile.push(sterneHtml(sb.schwierigkeit));
  const min = sb.minuten != null ? sb.minuten : b.duration_minutes;
  if (min != null) teile.push(`<span class="wk-meta-item">⏱ ${esc(String(min))} Min.</span>`);
  if (sb.modellklasse) teile.push(`<span class="wk-meta-item">Modell: ${esc(sb.modellklasse)}</span>`);
  if (sb.thinking) teile.push(`<span class="wk-meta-item">Thinking: ${esc(sb.thinking)}</span>`);
  return teile;
}

/* ---------- Voraussetzungen (W2): Legacy-Liste ODER strukturiert ---------- */
function benoetigtHtml(ben: WkSteckbrief["benoetigt"]): string {
  if (!ben) return "";
  if (Array.isArray(ben)) return ben.map((x) => `<li>${esc(x)}</li>`).join("");
  const li: string[] = [];
  (ben.koennen || []).forEach((x) => li.push(`<li><strong>Können:</strong> ${esc(x)}</li>`));
  (ben.mitbringen || []).forEach((x) => li.push(`<li><strong>Mitbringen:</strong> ${esc(x)}</li>`));
  (ben.zugaenge || []).forEach((z) => {
    const alt = (z.alternativen || []).length ? ` — Alternativen: ${z.alternativen!.map(esc).join(", ")}` : "";
    li.push(
      `<li><strong>Zugang:</strong> ${esc(z.dienst || "")}${z.wofuer ? ` (${esc(z.wofuer)})` : ""}${z.konto ? ` · ${esc(z.konto)}` : ""}${z.mindeststufe ? ` · ${esc(z.mindeststufe)}` : ""}${alt}</li>`,
    );
  });
  (ben.ausgabe_kis || []).forEach((a) => {
    const alt = (a.alternativen || []).length ? ` — Alternativen: ${a.alternativen!.map(esc).join(", ")}` : "";
    li.push(
      `<li><strong>KI für ${esc(a.typ || "Ausgabe")}:</strong> ${esc(a.empfohlen || "")}${alt}${a.mehr ? ` · siehe ${esc(a.mehr)}` : ""}</li>`,
    );
  });
  return li.join("");
}

function slugDateiname(s: string): string {
  return (
    String(s || "")
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9._-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "uebungsmaterial"
  );
}

/** Prompt-Text mit dezent hervorgehobenen [Variablen]. */
function promptHtml(text: string): string {
  return esc(String(text || "")).replace(/\[([^\]]+)\]/g, '<span class="wk-var">[$1]</span>');
}

/* Anzeigenamen der Prompt-Ziele (ziel=-Attribut, W1-Kritik Befund 1). */
const ZIEL_KI_NAMEN: Record<string, string> = {
  notebooklm: "NotebookLM",
  bildgenerator: "deine Bild-KI (z. B. Gemini/Nano Banana)",
  "websuche-ki": "eine KI mit Webzugang (z. B. ChatGPT mit Suche, Gemini)",
  extern: "die im Projekt empfohlene externe KI",
};

function promptKarte(p: WkPrompt | undefined, achtungVar: boolean, ctx?: WkCtx): string {
  if (!p || !p.text) return "";
  const original = String(p.text);
  const idAttr = p.id ? ` data-prompt-id="${escAttr(p.id)}"` : "";
  const zielKi = String(p.ziel_ki || "kurs-ki");
  // Personalisierung (Klasse A: GPT-Overlay) + Schritt-Eingaben (Klasse B: clientseitig).
  let text = original;
  let personalisiert = false;
  if (ctx && p.id) {
    const ov = personalText(ctx.mId, ctx.bId, p.id, original);
    if (ov) {
      text = ov;
      personalisiert = true;
    }
    text = setzeSchritt(text, ladeSchritt(ctx.mId, ctx.bId));
  }
  const restVar = /\[[^\]]+\]/.test(text); // nach dem Einsetzen noch offene [Platzhalter]?
  // Fremdes Ziel: KEIN Kurs-KI-Knopf (unser Chatbot kann z. B. keine Bilder).
  const fremd = zielKi !== "kurs-ki";
  const zielHinweis = fremd
    ? `<p class="wk-prompt-ziel"><span aria-hidden="true">↪</span> Diesen Prompt fügst du in <strong>${esc(ZIEL_KI_NAMEN[zielKi] || zielKi)}</strong> ein — nicht in die Kurs-KI.</p>`
    : "";
  const hasVarAttr = achtungVar && restVar ? ' data-has-var="1"' : "";
  const kiKnopf = fremd
    ? ""
    : `<button type="button" class="btn-coach wk-ask-ki" data-ask-ki="${escAttr(text)}"${hasVarAttr}><span aria-hidden="true">↗</span> In der Kurs-KI öffnen</button>`;
  const badge = personalisiert
    ? `<div class="wk-prompt-badge"><span class="wk-badge-tag">✦ auf dein Vorhaben angepasst</span><button type="button" class="wk-orig-toggle" data-wk-orig="${escAttr(p.id || "")}" data-wk-orig-text="${escAttr(original)}" aria-pressed="false">Original anzeigen</button></div>`
    : "";
  return `<div class="wk-prompt glass${personalisiert ? " is-personal" : ""}"${idAttr}>
    ${badge}
    <pre class="wk-prompt-text">${promptHtml(text)}</pre>
    ${zielHinweis}
    <div class="wk-prompt-actions">
      ${kiKnopf}
      <button type="button" class="btn-copy" data-copy="${escAttr(text)}" data-copy-label="Prompt kopiert ✓">Kopieren</button>
    </div>
  </div>`;
}

/* Schritt-Eingabefeld (Klasse B): Felder für Dokumente/Zwischenergebnisse, die
   DIESER Schritt-Prompt enthält. Clientseitig, OHNE GPT, OHNE Datenabfluss. */
function wkSchrittEingabe(s: WkSchritt, ctx?: WkCtx): string {
  if (!ctx || !s.prompt) return "";
  const t = String(s.prompt.text || "");
  const namen: string[] = [];
  (t.match(/\[[^\]]+\]/g) || []).forEach((m) => {
    const n = m.slice(1, -1);
    if (istSchrittPlatzhalter(n) && !namen.includes(n)) namen.push(n);
  });
  if (!namen.length) return "";
  const werte = ladeSchritt(ctx.mId, ctx.bId);
  const felder = namen
    .map(
      (n) => `<label class="wk-schritt-feld">
      <span class="wk-schritt-label">${esc(n)}</span>
      <textarea class="wk-schritt-input" data-wk-schritt="${escAttr(n)}" rows="3" placeholder="Hier einfügen oder eintippen …">${esc(werte[n] || "")}</textarea>
    </label>`,
    )
    .join("");
  return `<div class="wk-schritt-eingabe" data-wk-schritt-box data-module="${escAttr(ctx.mId)}" data-block="${escAttr(ctx.bId)}">
    <p class="wk-schritt-hinweis"><span aria-hidden="true">✎</span> Setze hier dein eigenes Material ein — es bleibt in deinem Browser und wird direkt in den Prompt oben eingefügt.</p>
    ${felder}
    <button type="button" class="btn-coach wk-schritt-apply" data-wk-schritt-apply>In den Prompt übernehmen</button>
  </div>`;
}

function schrittHtml(s: WkSchritt, ctx?: WkCtx): string {
  const hatVar = !!(s.prompt && /\[[^\]]+\]/.test(String(s.prompt.text || "")));
  const prompt = promptKarte(s.prompt, hatVar, ctx);
  const schrittEingabe = wkSchrittEingabe(s, ctx);
  const bewirktDetails = s.bewirkt
    ? `<details class="mitnehmen">
      <summary><span class="tag">Erklärung</span> Was bewirkt dieser Prompt?</summary>
      <p>${esc(s.bewirkt)}</p>${s.muster ? `<p class="wk-muster"><strong>Muster dahinter:</strong> ${esc(s.muster)}</p>` : ""}
    </details>`
    : "";
  const verhindertDetails = s.verhindert
    ? `<details class="mitnehmen wk-warn-details">
      <summary><span class="tag warn">Warnung</span> Was passiert, wenn du ihn weglässt?</summary>
      <p>${esc(s.verhindert)}</p>
    </details>`
    : "";
  const achten = s.achten
    ? `<p class="wk-achten"><span class="wk-achten-tag">Worauf du achtest</span> ${esc(s.achten)}</p>`
    : "";
  const hinweis = s.hinweis
    ? `<div class="callout-nb glass wk-hinweis"><span class="ico">●</span><div><p>${esc(s.hinweis)}</p></div></div>`
    : "";
  return `<article class="wk-step">
    <div class="wk-step-head">
      <div class="wk-step-nr">${esc(String(s.nr ?? ""))}</div>
      <div class="wk-step-title"><h3>${esc(s.titel || "")}</h3>${s.ziel ? `<p class="wk-step-ziel">${esc(s.ziel)}</p>` : ""}</div>
    </div>
    ${prompt}
    ${schrittEingabe}
    ${bewirktDetails || verhindertDetails ? `<div class="wk-step-erklaer">${bewirktDetails}${verhindertDetails}</div>` : ""}
    ${achten}
    ${hinweis}
  </article>`;
}

/** Anpass-Hinweis: welche [Variablen] über alle Schritte ersetzt werden. */
function anpassHinweis(b: ProjektPayload): string {
  const vars = new Set<string>();
  (b.schritte || []).forEach((s) => {
    if (s.prompt) (s.prompt.variablen || []).forEach((v) => vars.add(v));
  });
  if (!vars.size) return "";
  const chips = Array.from(vars)
    .map((v) => `<code class="wk-var">[${esc(v)}]</code>`)
    .join(" ");
  return `<div class="callout-nb glass wk-anpass"><span class="ico">◆</span><div>
    <h4>So machst du daraus dein eigenes Projekt</h4>
    <p>Ersetze in den Prompts die markierten Platzhalter durch deine echten Angaben — die Schrittfolge bleibt gleich:</p>
    <p class="wk-anpass-vars">${chips}</p>
  </div></div>`;
}

/* Intake-Box „Mach es zu DEINEM Projekt" (Web app.js intakeBox, Z. 868-921).
   Zeigt die kuratierten Fragen (aus b.personalisierung.fragen) + optional Mikrofon +
   Datenschutz-/Postkarten-Hinweis. Erscheint nur, wenn das Projekt Fragen mitbringt.
   Bei vorhandenem Overlay: Bestätigung „verstanden" + Ändern/Zurücksetzen.
   WORTLAUT 1:1 zum Web. */
function intakeBox(b: ProjektPayload, ctx: WkCtx): string {
  const pers = b.personalisierung;
  const fragen = pers && Array.isArray(pers.fragen) ? pers.fragen : [];
  if (!fragen.length) return ""; // Projekt ohne kuratierte Fragen → keine Box
  const { mId, bId } = ctx;
  const data = ladePersonal(mId, bId);
  const eingeloggt = isAuthenticated.value;
  if (data && data.verstanden) {
    const offen = (data.offen || []).map((o) => `<li>${esc(o)}</li>`).join("");
    return `<section class="wk-vorhaben is-fertig glass" data-wk-intake data-module="${escAttr(mId)}" data-block="${escAttr(bId)}">
      <div class="wk-vorhaben-head"><span class="wk-vorhaben-tag">✦ Auf dein Vorhaben angepasst</span></div>
      <p class="wk-vorhaben-verstanden"><strong>So habe ich dein Vorhaben verstanden:</strong> ${esc(data.verstanden)}</p>
      ${offen ? `<div class="wk-vorhaben-offen"><p>Das konnte ich noch nicht füllen — trag es beim jeweiligen Schritt selbst ein:</p><ul>${offen}</ul></div>` : ""}
      <p class="wk-vorhaben-hinweis">Die Prompts unten tragen jetzt dein Vorhaben. Stimmt etwas nicht, ändere deine Angaben.</p>
      <div class="wk-vorhaben-actions">
        <button type="button" class="btn-ghost" data-wk-aendern>Angaben ändern</button>
        <button type="button" class="btn-ghost wk-reset" data-wk-reset>Personalisierung zurücksetzen</button>
      </div>
    </section>`;
  }
  // Bei „Angaben ändern" sind Antworten/Freitext gespeichert → Felder vorbefüllen.
  const vorAntw: Record<string, string> = {};
  (data?.antworten || []).forEach((a) => {
    if (a.frage_id) vorAntw[a.frage_id] = a.text || "";
  });
  const vorFreitext = data?.freitext || "";
  const felder = fragen
    .map((f, i) => {
      const id = escAttr(f.id || "f" + i);
      const wert = esc(vorAntw[f.id || "f" + i] || "");
      // Mikro auf JEDEM Feld (Auftraggeber 2026-06-18: „nur manche Felder haben
      // Spracheingabe") — nicht mehr abhängig vom f.mikro-Flag aus dem Content.
      const mikro = `<button type="button" class="wk-mikro" data-wk-mikro="${id}" title="Antwort einsprechen" aria-label="Antwort einsprechen">🎤</button>`;
      return `<div class="wk-frage">
      <label class="wk-frage-label" for="wk-q-${id}">${esc(f.frage || "")}</label>
      <div class="wk-frage-eingabe">
        <textarea id="wk-q-${id}" class="wk-frage-input" data-wk-frage="${id}" data-wk-frage-text="${escAttr(f.frage || "")}" rows="2" placeholder="Deine Antwort …"${eingeloggt ? "" : " disabled"}>${wert}</textarea>
        ${eingeloggt ? mikro : ""}
      </div>
    </div>`;
    })
    .join("");
  const doku = pers && pers.dokument_feld
    ? `<div class="wk-frage">
      <label class="wk-frage-label" for="wk-doku-${escAttr(bId)}">Kurz: Was für ein Dokument willst du bearbeiten? (Art und Zweck — das ganze Dokument fügst du später direkt beim Schritt ein.)</label>
      <div class="wk-frage-eingabe">
        <textarea id="wk-doku-${escAttr(bId)}" class="wk-frage-input" data-wk-freitext rows="2" placeholder="z. B. ein Mietvertrag, ich will alle Fristen und Pflichten erkennen"${eingeloggt ? "" : " disabled"}>${esc(vorFreitext)}</textarea>
        ${eingeloggt ? `<button type="button" class="wk-mikro" data-wk-mikro="doku" title="Antwort einsprechen" aria-label="Antwort einsprechen">🎤</button>` : ""}
      </div>
    </div>`
    : "";
  const gesperrt = eingeloggt
    ? ""
    : `<p class="wk-vorhaben-login">Melde dich an, um dein Vorhaben einzugeben — dann passe ich die Prompts für dich an.</p>`;
  return `<section class="wk-vorhaben glass" data-wk-intake data-module="${escAttr(mId)}" data-block="${escAttr(bId)}">
    <details class="wk-vorhaben-details" open>
      <summary class="wk-vorhaben-summary"><span class="wk-vorhaben-tag">✦ Mach es zu DEINEM Projekt</span> <span class="wk-vorhaben-frei">freiwillig</span></summary>
      <p class="wk-vorhaben-intro">Beantworte mir kurz ein paar Fragen zu deinem Vorhaben — dann schreibe ich alle Prompts dieses Projekts so um, dass dein echter Fall schon drinsteht. Oder überspring das und arbeite mit dem Beispiel — die Schritte sind dieselben.</p>
      <div class="wk-vorhaben-fragen">${felder}${doku}</div>
      <p class="wk-vorhaben-datenschutz"><strong>Kurz zu deinen Daten:</strong> Was du hier eingibst, schickt der Kurs <strong>einmalig</strong> an die Kurs-KI, um die Prompts anzupassen. Auf dem Server speichern wir deine Texte dabei <strong>nicht</strong> — das Ergebnis liegt nur in deinem Browser. Es gilt die <strong>Postkarten-Regel</strong>: keine echten Namen, Kundendaten oder Geheimnisse — Platzhalter wie [Name Kunde] reichen. Das Anpassen zählt wie eine Chat-Nachricht auf dein Kontingent.</p>
      ${gesperrt}
      <div class="wk-vorhaben-actions">
        <button type="button" class="btn-coach wk-anpassen" data-wk-anpassen${eingeloggt ? "" : " disabled"}><span aria-hidden="true">✦</span> Prompts auf mein Vorhaben anpassen</button>
      </div>
      <p class="wk-vorhaben-status" data-wk-status aria-live="polite"></p>
    </details>
  </section>`;
}

/**
 * Projekt-Kapitel → Schritt-Segmente (paginiert wie Lese-Unterkapitel):
 * Überblick (Steckbrief + Material + Intro + Intake/Anpass-Hinweis) → je Prompt EIN
 * Schritt → optional Vergleich → Transfer. 1:1-Port von app.js projektSegmente.
 *
 * `moduleId` ist für die Personalisierung nötig (localStorage-Schlüssel je Modul+
 * Block). Fehlt sie, fällt der Renderer auf b.module_id zurück (Roh-Payload).
 */
export function projektSegmente(b: ProjektPayload, moduleId?: string): UkSegment[] {
  const ctx: WkCtx = { mId: moduleId || String(b.module_id || ""), bId: b.id };
  const sb = b.steckbrief || {};
  const meta = projektMeta(b).join("");
  const lernen = (b.was_du_lernst || []).map((x) => `<li>${esc(x)}</li>`).join("");
  const benoetigt = benoetigtHtml(sb.benoetigt);
  const mat = b.material || null;
  const matName = slugDateiname((mat && (mat.titel || b.titel || b.title)) || "uebungsmaterial");
  const materialBox =
    mat && mat.text
      ? `<div class="wk-material">
      <div class="wk-material-head">
        <span class="wk-material-tag">📄 Übungsmaterial</span>
        <strong class="wk-material-titel">${esc(mat.titel || "Beispiel-Dokument")}</strong>
      </div>
      <p class="wk-material-erkl">Mit diesem Beispiel-Dokument übst du dieses Projekt. Hol es
      dir auf einem der zwei Wege — und gib es dann der KI, wie im jeweiligen Schritt beschrieben.</p>
      <div class="wk-material-actions">
        <button type="button" class="btn-coach wk-material-copy" data-copy="${escAttr(mat.text)}" data-copy-label="Übungsmaterial kopiert ✓"><span aria-hidden="true">⧉</span> Kopieren</button>
        <button type="button" class="btn-copy" data-material-download="${escAttr(mat.text)}" data-material-name="${escAttr(matName)}"><span aria-hidden="true">⬇</span> Als Datei laden</button>
      </div>
      <details class="mitnehmen"><summary>${esc(mat.titel || "Beispiel-Dokument")} anzeigen</summary>
        <div class="lesson wk-material-body">${sanitizeHtml(mat.html || `<pre>${esc(mat.text)}</pre>`)}</div>
      </details>
    </div>`
      : "";
  const kopf = `<section class="wk-steckbrief glass">
    ${meta ? `<div class="wk-badges">${meta}</div>` : ""}
    ${
      sb.endprodukt || benoetigt || lernen
        ? `<div class="wk-steckbrief-details">
      ${sb.endprodukt ? `<details class="mitnehmen wk-fakt wk-fakt-ergebnis" open><summary><span class="tag">Ergebnis</span> Was kommt raus?</summary><p>${esc(sb.endprodukt)}</p></details>` : ""}
      ${benoetigt ? `<details class="mitnehmen wk-fakt wk-fakt-vorbereitung" open><summary><span class="tag">Vorbereitung</span> Was du brauchst</summary><ul class="wk-list">${benoetigt}</ul></details>` : ""}
      ${lernen ? `<details class="mitnehmen wk-fakt wk-fakt-lernziel"><summary><span class="tag">Lernziel</span> Was du lernst</summary><ul class="wk-list">${lernen}</ul></details>` : ""}
    </div>`
        : ""
    }
    ${materialBox}
  </section>`;
  const introHtml = (b.intro_html as string) || b.text || "";
  const intro = introHtml ? `<div class="lesson glass wk-intro">${sanitizeHtml(introHtml)}</div>` : "";
  // Hat das Projekt kuratierte Personalisierungs-Fragen, zeigt die Intake-Box
  // („Mach es zu DEINEM Projekt"); sonst der generische Platzhalter-Hinweis.
  const intake = intakeBox(b, ctx);
  const anpass = intake || anpassHinweis(b);
  const segs: UkSegment[] = [];
  const ueberblick = `${kopf}${intro}${anpass}`.trim();
  if (ueberblick) segs.push({ key: "ueberblick", title: "Überblick", html: `<div class="wk-overview">${ueberblick}</div>` });
  (b.schritte || []).forEach((s, i) => {
    segs.push({
      key: `schritt-${s.nr ?? i + 1}`,
      title: s.titel || `Schritt ${i + 1}`,
      html: `<section class="wk-steps">${schrittHtml(s, ctx)}</section>`,
    });
  });
  if (b.probier) {
    const p = b.probier;
    const naiv = p.prompt_naiv ? promptKarte(p.prompt_naiv, false, ctx) : "";
    segs.push({
      key: "vergleich",
      title: p.titel || "Zum Vergleich",
      html: `<section class="wk-probier-step">
      <p class="wk-probier-tag">Zum Vergleich · nicht empfohlen</p>
      <h3>${esc(p.titel || "Probier den Unterschied")}</h3>
      ${p.text_html ? `<div class="lesson">${sanitizeHtml(p.text_html)}</div>` : ""}
      ${naiv}
    </section>`,
    });
  }
  if (b.transfer) {
    const t = b.transfer;
    segs.push({
      key: "transfer",
      title: "Zum Mitdenken",
      html: `<section class="wk-transfer glass">
      ${t.text_html ? `<div class="lesson">${sanitizeHtml(t.text_html)}</div>` : ""}
      ${t.frage ? `<div class="wk-transfer-frage"><span class="wk-transfer-tag">Zum Mitdenken</span><p>${esc(t.frage)}</p></div>` : ""}
    </section>`,
    });
  }
  if (!segs.length)
    segs.push({
      key: "projekt",
      title: b.titel || b.title || "Projekt",
      html: `<div class="lesson glass">${sanitizeHtml(introHtml)}</div>`,
    });
  return segs;
}

/** Erstes Nicht-Projekt-Kapitel eines Moduls mit Projekten = Werkstatt-Intro (nicht paginiert). */
export function istWerkstattIntro(mod: CourseModule | undefined, chapterId: string): boolean {
  if (!mod) return false;
  const hatProjekte = mod.chapters.some((c) => c.layout === "projekt");
  if (!hatProjekte) return false;
  const ersterNichtProjekt = mod.chapters.find((c) => c.layout !== "projekt");
  return !!ersterNichtProjekt && ersterNichtProjekt.id === chapterId;
}

/** „Modul starten" bei Projekt-Modulen: erstes Nicht-Projekt-Kapitel (Web: modulStartBlock). */
export function modulStartChapter(mod: CourseModule): ChapterRef | undefined {
  if (mod.chapters.some((c) => c.layout === "projekt"))
    return mod.chapters.find((c) => c.layout !== "projekt") || mod.chapters[0];
  return mod.chapters[0];
}

/* ---------- Projekt-Übersicht (Karten) — Preact-Komponente ---------- */

function KartenDetail({ courseId, mod, ch }: { courseId: string; mod: CourseModule; ch: ChapterRef }) {
  const [detail, setDetail] = useState<ProjektPayload | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    loadChapter(mod.id, ch.id, courseId)
      .then((c) => alive && setDetail((c.raw as ProjektPayload) ?? null))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [mod.id, ch.id]);
  const sb = detail?.steckbrief || {};
  const lernen = detail?.was_du_lernst || [];
  return (
    <>
      {sb.endprodukt ? (
        <p>
          <strong>Das kommt raus:</strong> {sb.endprodukt}
        </p>
      ) : null}
      {lernen.length ? (
        <>
          <p>
            <strong>Das lernst du:</strong>
          </p>
          <ul class="wk-list">
            {lernen.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </>
      ) : null}
      {!detail && !failed ? <p class="text-muted">Lädt …</p> : null}
    </>
  );
}

export function WerkstattUebersicht({
  courseId,
  mod,
  onOpen,
}: {
  courseId: string;
  mod: CourseModule;
  onOpen: (m: string, b: string) => void;
}) {
  const projekte = mod.chapters.filter((c) => c.layout === "projekt");
  if (!projekte.length) return null;
  return (
    <section class="wk-overview" aria-label="Projekte in der Werkstatt">
      <div class="callout-nb glass">
        <span class="ico">◆</span>
        <div>
          <h4>Wähle dein Projekt</h4>
          <p>
            Jedes Projekt ist eine kleine Werkstatt: ein echtes Ergebnis, Schritt für Schritt mit
            fertigen Prompts. Klapp auf, worum es geht — und leg los.
          </p>
        </div>
      </div>
      <div class="wk-card-grid">
        {projekte.map((ch, i) => (
          <article key={ch.id} class="wk-card glass">
            <button type="button" class="wk-card-open" onClick={() => onOpen(mod.id, ch.id)}>
              <span class="wk-card-nr">Projekt {i + 1}</span>
              <span class="wk-card-titel">{ch.title}</span>
              {ch.durationMinutes != null ? (
                <span class="wk-card-meta">
                  <span class="wk-meta-item">⏱ {ch.durationMinutes} Min.</span>
                </span>
              ) : null}
              {ch.summary ? <span class="wk-card-summary">{ch.summary}</span> : null}
            </button>
            <details class="mitnehmen wk-card-mehr">
              <summary>
                <span class="tag">Worum geht's</span> was lerne ich?
              </summary>
              <KartenDetail courseId={courseId} mod={mod} ch={ch} />
              <p>
                <button type="button" class="btn-coach wk-card-go" onClick={() => onOpen(mod.id, ch.id)}>
                  Projekt öffnen
                </button>
              </p>
            </details>
          </article>
        ))}
      </div>
    </section>
  );
}
